import { ClickoutUrl } from '@/helpers/clickout-helper';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useExperiment } from '@/libraries/experiment';
import {
	ExperimentAffiliationPlacement,
	getExperimentAffiliateTag,
} from '@/libraries/experiment/experiment-affiliation';
import { ObjectType } from '@/@types/graphql-types';
import { storeToRefs } from 'pinia';

// Free trial subscription providers — use their own short name as tag identifier
const FT_PROVIDERS = ['atp', 'amp', 'prv', 'hlu', 'cru', 'dnp'];

// Amazon TVOD — gets the Amazon Associates prime suffix
const AMAZON_TVOD_PROVIDERS = ['amz'];

// Apple TVOD (iTunes) — same 'tvod' tag but no prime suffix
const APPLE_TVOD_PROVIDERS = ['itu'];

function getProviderTagInfo(
	shortName: string,
	technicalName: string | undefined
): { tagName: string; associatesProvider: string | null } | null {
	if (FT_PROVIDERS.includes(shortName)) return { tagName: shortName, associatesProvider: shortName };
	if (AMAZON_TVOD_PROVIDERS.includes(shortName)) return { tagName: 'tvod', associatesProvider: shortName };
	if (APPLE_TVOD_PROVIDERS.includes(shortName)) return { tagName: 'tvod', associatesProvider: null };
	if (technicalName?.startsWith('amazon') && technicalName !== 'amazonprime')
		return { tagName: 'amzch', associatesProvider: technicalName };
	if (technicalName?.startsWith('apple') && technicalName !== 'appletvplus')
		return { tagName: 'atpch', associatesProvider: null };
	return null;
}

export function useSofterFreeTrialV2Exp() {
	const { activeVariantWithControlGroup: variant, experimentInfo } = useExperiment('EXP_SOFTER_FREE_TRIAL_V2');

	const { country } = storeToRefs(useLanguageStore());

	function setSofterFreeTrialV2Tags(
		url: ClickoutUrl,
		shortName: string | undefined,
		placement: ExperimentAffiliationPlacement,
		objectType?: ObjectType,
		technicalName?: string
	) {
		if (!variant.value || !shortName) return url;

		const providerTagInfo = getProviderTagInfo(shortName, technicalName);
		if (!providerTagInfo) return url;

		const affiliationShortId = experimentInfo.value.affiliationShortId as string;

		const tag = getExperimentAffiliateTag({
			affiliationShortId,
			variant: variant.value,
			country: country.value,
			placement,
			providerShortName: providerTagInfo.associatesProvider,
			objectType,
			tagProviderName: providerTagInfo.tagName,
			technicalName,
		});
		return url.set('uct_ctag', tag);
	}

	return {
		setSofterFreeTrialV2Tags,
	};
}
